import { useState } from "react";
import { Modal } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import LiveLister from "../../components/Lister/LiveLister";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PurposeEnum } from "../../models/Purpose";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { generateRemoteUrl, languageOptions } from "./Modules.function";
import CreateModuleForm from "./components/CreateModuleForm/CreateModuleForm";

export const Modules = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [selectedLanguageFilters, setSelectedLanguageFilters] = useState<any[]>([]);
  const [selectedPurposeFilters, setSelectedPurposeFilters] = useState<PurposeEnum[]>([]);
  const [showCreateModule, setShowCreateModule] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const columns = [
    { header: translate(language, "GLOBAL.ID"), key: "id", sortable: true },
    { header: translate(language, "GLOBAL.NAME"), key: "name", sortable: true },
    { header: translate(language, "GLOBAL.DESCRIPTION"), key: "description" },
    { header: translate(language, "PAGES.MODULES.PROGRAMMING_LANGUAGE"), key: "programming_language" },
    { header: translate(language, "GLOBAL.LANGUAGE"), key: "language" },
    { header: translate(language, "PAGES.MODULES.PURPOSE"), key: "purpose" },
    {
      header: translate(language, "PAGES.MODULES.OWNER"),
      key: "owner",
      sortable: true,
      render: (module: any) => (
        <span
          className="clickable text-primary"
          onClick={(event) => completeNavigate(event, navigate, `/user/${module.owner}`)}
        >
          {module.owner}
        </span>
      ),
    },
    {
      header: translate(language, "GLOBAL.STATUS"),
      key: "is_deprecated",
      render: (module: any) => (
        <div className="m-regular">
          {module.is_deprecated ? (
            <span className="badge bg-secondary">{translate(language, "GLOBAL.DEPRECATED")}</span>
          ) : (
            <span className="badge bg-success">{translate(language, "GLOBAL.ACTIVE")}</span>
          )}
        </div>
      ),
    },
    {
      header: translate(language, "PAGES.MODULES.CREATED_AT"),
      key: "dt_created",
      sortable: true,
      render: (module: any) => <span>{formatDateHumanly(language, module.created_at)}</span>,
    },
    {
      header: translate(language, "PAGES.MODULES.UPDATED_AT"),
      key: "dt_updated",
      sortable: true,
      render: (module: any) => (
        <span>{module.updated_at ? formatDateHumanly(language, module.updated_at) : "N/A"}</span>
      ),
    },
  ];

  return (
    <div className="m-5 position-relative">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-3">
        <div className="d-flex align-items-center">
          <h2 className="mb-0 me-3">{translate(language, "PAGES.MODULES.TITLE")}</h2>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div>
            <MultiFilterDropdown
              tags={languageOptions(language)}
              onSelectTags={(languages) => setSelectedLanguageFilters(languages)}
              language={language}
              inputTitle={translate(language, "GLOBAL.LANGUAGE")}
              inputLabel={translate(language, "PAGES.MODULES.FILTERS.INPUT_LABEL")}
              inputPlaceholder={translate(language, "PAGES.MODULES.FILTERS.INPUT_PLACEHOLDER")}
              showSearchBar
            />
          </div>
          <div>
            <MultiFilterDropdown
              tags={Object.values(PurposeEnum).map((purpose) => ({
                display: purpose,
                value: purpose.toLowerCase(),
              }))}
              onSelectTags={(purposes) => setSelectedPurposeFilters(purposes as PurposeEnum[])}
              inputTitle="Purpose"
              showSearchBar
              language={language}
            />
          </div>
          <div className="d-flex align-items-center">
            <DstButton
              btnClass="d-flex align-items-center"
              btnImageBefore={
                <Form.Check
                  type="switch"
                  className="check-box s-bold me-2"
                  id="show-only-active-switch"
                  checked={showOnlyActive}
                  onChange={() => setShowOnlyActive(!showOnlyActive)}
                />
              }
              value={translate(language, "PAGES.MODULES.SHOW_ONLY_ACTIVE")}
              variant="light"
              clickFunction={() => setShowOnlyActive(!showOnlyActive)}
            />
          </div>
          <div className="d-flex align-items-center">
            <DstButton
              value={translate(language, "PAGES.MODULES.CREATE_MODULE")}
              variant="primary"
              btnImageBefore={<span className="dst-icon-plus-circle me-2" />}
              clickFunction={() => setShowCreateModule(true)}
            />
          </div>
        </div>
      </div>
      <LiveLister
        key={refreshKey}
        remote={generateRemoteUrl(searchQuery, showOnlyActive, selectedLanguageFilters, selectedPurposeFilters)}
        remoteKey="modules"
        remoteTransform={(module: any) => ({
          id: module.id,
          name: module.name,
          description: module.description,
          programming_language: module.programming_language,
          language: module.language,
          purpose: module.purpose,
          is_deprecated: module.is_deprecated,
          created_at: module.dt_created,
          updated_at: module.dt_updated,
          owner: module.owner,
        })}
        columns={columns}
        additionalButton={(item: any) => (
          <span
            className="clickable xs-regular"
            onClick={(event) => completeNavigate(event, navigate, `/module/${item.id}`)}
          >
            <span className="dst-icon-arrow-narrow-right m-regular" />
          </span>
        )}
        showHeader
        pagination
        itemsPerPage={20}
      />
      <Modal show={showCreateModule} onHide={() => setShowCreateModule(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{translate(language, "PAGES.MODULES.CREATE_MODULE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateModuleForm
            onSuccess={() => {
              setShowCreateModule(false);
              setRefreshKey((prev) => prev + 1);
            }}
            onCancel={() => setShowCreateModule(false)}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Modules;
